import React, {useEffect, useMemo, useState} from "react";
import {
    Badge,
    Button,
    Container,
    Group,
    Loader,
    Paper,
    Select,
    Stack,
    Switch,
    Table,
    Text,
    TextInput,
    Title,
    useMantineColorScheme,
    Pagination,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {IconArrowLeft, IconFlag, IconPlus, IconTrash} from "@tabler/icons-react";
import {useNavigate, useParams} from "react-router-dom";
import {USER_FLAG_OPTIONS, UserFlag, UserFlagBadge} from "components/Badges";
import {
    useAdminUserByIdQuery,
    useAdminAddUserMemoMutation,
    useAdminUserMemosLazyQuery,
    AdminUserMemosQuery,
    useGetRolesQuery,
    useAssignRoleToUserMutation,
    useUnassignRoleFromUserMutation,
    useSetUserFlagsMutation,
} from "generated/graphql";
/**
 * UserEdit (Admin)
 *
 * Mocked admin page to manage a specific user. This file intentionally
 * co-locates small, focused components to keep the page maintainable while
 * avoiding creating additional files.
 *
 * Sections:
 * - HeaderSection: Inline editable name, ID display, Active toggle, current flags badges
 * - RolesSection: Add/remove roles from the user
 * - FlagsSection: Toggle administrative flags (e.g., Suspended, Banned)
 * - NotesSection: Add internal admin notes (notes cannot be removed per requirements)
 * - BottomActions: Save/Cancel buttons
 *
 * All state continues to live in this file and is passed into the components
 * via props so behavior is unchanged. The UI remains mocked.
 *
 * TODO (API wiring):
 * - Load user details (name, roles, notes, flags) by userId
 * - Load available roles from API
 * - Persist role changes, notes, and flags via API
 */

/**
 * HeaderSection
 * Displays the username (not editable), the user ID, current flag badges, and the Active toggle.
 * Includes a "Flag Inappropriate" button for admins to flag offensive usernames (stubbed).
 */
function HeaderSection(props: {
    name: string;
    userId?: string;
    isDark: boolean;
    flags: readonly UserFlag[];
    active: boolean;
    onToggleActive: (checked: boolean) => void;
    onFlagInappropriate: () => void;
}) {
    const {name, userId, isDark, flags, active, onToggleActive, onFlagInappropriate} = props;

    return (
        <Group justify="space-between" align="center" className="w-full">
            <div>
                <Group gap="xs" align="center">
                    <Title c={isDark ? "white" : "black"}>{name}</Title>
                    <Button size="xs" variant="outline" color="orange" leftSection={<IconFlag size={14}/>}
                            onClick={onFlagInappropriate}>
                        Flag Inappropriate
                    </Button>
                </Group>
                <Text size="sm" c={isDark ? "gray.4" : "gray.7"}>User ID: {userId}</Text>
            </div>
            <Group gap="md" align="center" className="ml-auto">
                {flags.map((f) => (
                    <UserFlagBadge key={f} type={f}/>
                ))}
                <Switch checked={active} onChange={(e) => onToggleActive(e.currentTarget.checked)} label="Active"/>
            </Group>
        </Group>
    );
}

/** Role object with ID for mutations */
type RoleInfo = { id: string; name: string; color?: string | null };

/**
 * RolesSection
 * Shows the list of current roles and a control to add a role from the available set.
 * Consumers provide current roles, the available roles, and add/remove handlers.
 */
function RolesSection(props: {
    roles: RoleInfo[];
    availableRoles: RoleInfo[];
    roleToAdd: string | null;
    setRoleToAdd: (v: string | null) => void;
    addRole: () => void;
    removeRole: (roleId: string) => void;
    isDark: boolean;
    isAddingRole: boolean;
    isRemovingRole: boolean;
}) {
    const {roles, availableRoles, roleToAdd, setRoleToAdd, addRole, removeRole, isDark, isAddingRole, isRemovingRole} = props;
    // Filter out roles the user already has
    const assignedRoleIds = new Set(roles.map(r => r.id));
    const rolesNotAssigned = availableRoles.filter(r => !assignedRoleIds.has(r.id));

    return (
        <Paper withBorder radius="md" p="lg" shadow="sm" className="tw-paper tw-text-default">
            <Stack gap="sm">
                <Group justify="space-between" align="center">
                    <Text fw={600}>Roles</Text>
                    <Group>
                        <Select
                            placeholder="Select role to add"
                            data={rolesNotAssigned.map((r) => ({value: r.id, label: r.name}))}
                            value={roleToAdd}
                            onChange={setRoleToAdd}
                            searchable
                            nothingFoundMessage="No roles available"
                            w={220}
                            disabled={isAddingRole || isRemovingRole}
                        />
                        <Button
                            leftSection={<IconPlus size={16}/>}
                            onClick={addRole}
                            disabled={!roleToAdd || isAddingRole || isRemovingRole}
                            loading={isAddingRole}
                        >
                            Add Role
                        </Button>
                    </Group>
                </Group>
                <Group gap={8} wrap="wrap">
                    {roles.length > 0 ? (
                        roles.map((r) => (
                            <Badge
                                key={r.id}
                                variant="light"
                                color={r.color ? undefined : "gray"}
                                style={r.color ? { backgroundColor: `${r.color}20`, borderColor: r.color, color: r.color } : undefined}
                                className={!r.color ? "bg-gray-200 text-gray-900 border border-gray-300 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-500" : undefined}
                                rightSection={
                                    <Button
                                        size="compact-xs"
                                        variant="subtle"
                                        color="red"
                                        onClick={() => removeRole(r.id)}
                                        disabled={isRemovingRole || isAddingRole}
                                    >
                                        <IconTrash size={12}/>
                                    </Button>
                                }
                            >
                                {r.name}
                            </Badge>
                        ))
                    ) : (
                        <Text size="sm" c={isDark ? "gray.5" : "gray.6"}>
                            No roles assigned.
                        </Text>
                    )}
                </Group>
            </Stack>
        </Paper>
    );
}

/**
 * FlagsSection
 * Displays a compact action group to toggle predefined administrative flags for the user.
 * Note: Button copy is phrased for admins while underlying values remain the same.
 */
function FlagsSection(props: {
    flags: readonly UserFlag[];
    flagOptions: readonly UserFlag[];
    toggleFlag: (flag: UserFlag) => void;
    isDark: boolean;
    isLoading?: boolean;
}) {
    const {flags, flagOptions, toggleFlag, isDark, isLoading = false} = props;
    return (
        <Paper withBorder radius="md" p="lg" shadow="sm" className="tw-paper tw-text-default">
            <Stack gap="sm">
                <Text fw={600}>Flags</Text>
                <Group gap="sm" wrap="wrap">
                    {flagOptions.map((f) => {
                        const activeFlag = flags.includes(f);
                        return (
                            (() => {
                                const color = f === "Banned" ? "red" : f === "Suspended" ? "yellow" : "grape";
                                // @ts-ignore
                                const isBright = color === "yellow" || "orange" === color || color === "lime";
                                const variant = activeFlag ? "filled" : "light";
                                const textColor = activeFlag && isBright ? "gray.8" : undefined;

                                // Human-friendly admin phrasing while preserving underlying flag values
                                const label = (() => {
                                    switch (f) {
                                        case "Banned":
                                            return activeFlag ? "Unban Player" : "Ban Player";
                                        case "Suspended":
                                            return activeFlag ? "Remove Suspension" : "Suspend Player";
                                        case "Watchlist":
                                            // Display as Investigation in admin UI
                                            return activeFlag ? "Remove Investigation" : "Add Investigation";
                                        default:
                                            return activeFlag ? `Remove ${f}` : `Add ${f}`;
                                    }
                                })();

                                return (
                                    <Button
                                        key={f}
                                        variant={variant}
                                        color={color}
                                        c={textColor}
                                        className={
                                            activeFlag
                                                ? (
                                                    color === 'red'
                                                        ? 'bg-red-600 hover:bg-red-700 text-white'
                                                        : color === 'yellow'
                                                            ? 'bg-yellow-400 hover:bg-yellow-500 text-gray-900'
                                                            : 'bg-purple-600 hover:bg-purple-700 text-white'
                                                )
                                                : (
                                                    color === 'red'
                                                        ? 'bg-red-100 hover:bg-red-200 dark:bg-red-900/40 dark:hover:bg-red-900/60 text-red-900 dark:text-red-100'
                                                        : color === 'yellow'
                                                            ? 'bg-yellow-100 hover:bg-yellow-200 dark:bg-yellow-900/40 dark:hover:bg-yellow-900/60 text-yellow-900 dark:text-yellow-100'
                                                            : 'bg-purple-100 hover:bg-purple-200 dark:bg-purple-900/40 dark:hover:bg-purple-900/60 text-purple-900 dark:text-purple-100'
                                                )
                                        }
                                        leftSection={<IconFlag size={14}/>}
                                        onClick={() => toggleFlag(f)}
                                        disabled={isLoading}
                                        loading={isLoading}
                                    >
                                        {label}
                                    </Button>
                                );
                            })()
                        );
                    })}
                </Group>
                <Text size="sm" c={isDark ? "gray.5" : "gray.6"}>
                    Flags are administrative markers and may affect user access in the system.
                </Text>
            </Stack>
        </Paper>
    );
}

/**
 * NotesSection
 * Lets admins add internal notes for a user. Notes are immutable (no removal UI).
 */
function NotesSection(props: {
    notes: { id: string; text: string; createdAt: string; author: string }[];
    totalCount: number;
    newNote: string;
    setNewNote: (v: string) => void;
    addNote: () => void;
    isAdding: boolean;
    isDark: boolean;
    page: number;
    pageCount: number;
    onPageChange: (page: number) => void;
    pageSize: number;
    isLoadingNotes: boolean;
}) {
    const {notes, totalCount, newNote, setNewNote, addNote, isAdding, isDark, page, pageCount, onPageChange, pageSize, isLoadingNotes} = props;

    // Two-step confirmation state for adding a note
    const [isConfirming, setIsConfirming] = useState(false);
    const [isArmed, setIsArmed] = useState(false); // becomes true 200ms after showing confirm button
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const armTimeoutRef = React.useRef<number | null>(null);

    // When text changes, cancel confirmation state
    useEffect(() => {
        if (isConfirming) {
            setIsConfirming(false);
            setIsArmed(false);
            if (armTimeoutRef.current) {
                window.clearTimeout(armTimeoutRef.current);
                armTimeoutRef.current = null;
            }
        }
    }, [newNote]);

    // Outside click handler to cancel confirmation
    useEffect(() => {
        if (!isConfirming) return;
        function onDocClick(e: MouseEvent) {
            if (!containerRef.current) return;
            if (!containerRef.current.contains(e.target as Node)) {
                setIsConfirming(false);
                setIsArmed(false);
                if (armTimeoutRef.current) {
                    window.clearTimeout(armTimeoutRef.current);
                    armTimeoutRef.current = null;
                }
            }
        }
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, [isConfirming]);

    function startConfirm() {
        if (isAdding) return;
        setIsConfirming(true);
        setIsArmed(false);
        if (armTimeoutRef.current) window.clearTimeout(armTimeoutRef.current);
        armTimeoutRef.current = window.setTimeout(() => setIsArmed(true), 200);
    }

    async function onConfirm() {
        if (!isArmed || isAdding) return;
        await addNote();
        // After attempting to add, reset confirm state regardless of outcome
        setIsConfirming(false);
        setIsArmed(false);
    }

    return (
        <Paper withBorder radius="md" p="lg" shadow="sm" className="tw-paper tw-text-default">
            <Stack gap="sm">
                <Text fw={600}>Admin Notes</Text>
                {/* Full-width row with input stretching and action area aligned right */}
                <div ref={containerRef} style={{display: 'flex', gap: 'var(--mantine-spacing-sm)', alignItems: 'stretch'}}>
                    <TextInput
                        placeholder="Add an internal note about this user"
                        value={newNote}
                        onChange={(e) => setNewNote(e.currentTarget.value)}
                        size="md"
                        style={{flex: 1}}
                        disabled={isAdding}
                    />
                    {/* Action area: spinner while submitting, otherwise two-step button */}
                    {isAdding ? (
                        <div style={{display: 'flex', alignItems: 'center', padding: '0 12px'}}>
                            <Loader size="sm" />
                        </div>
                    ) : (
                        !isConfirming ? (
                            <Button size="md" leftSection={<IconPlus size={16}/>} onClick={startConfirm}
                                    disabled={!newNote.trim()}>
                                Add Note
                            </Button>
                        ) : (
                            <Button size="md" color="orange" onClick={onConfirm} disabled={!isArmed}>
                                {isArmed ? "Are you sure?" : "Preparing..."}
                            </Button>
                        )
                    )}
                </div>
                <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                    <Table striped highlightOnHover withTableBorder withColumnBorders horizontalSpacing="lg"
                           verticalSpacing="sm" stickyHeader>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>Note</Table.Th>
                                <Table.Th>Operator</Table.Th>
                                <Table.Th>Created</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {isLoadingNotes && (
                                <Table.Tr>
                                    <Table.Td colSpan={3}>
                                        <Group justify="center" align="center" gap="xs">
                                            <Loader size="sm" />
                                            <Text size="sm" c={isDark ? "gray.5" : "gray.6"}>Loading notes...</Text>
                                        </Group>
                                    </Table.Td>
                                </Table.Tr>
                            )}
                            {!isLoadingNotes && notes.map((n) => (
                                <Table.Tr key={n.id}>
                                    <Table.Td>{n.text}</Table.Td>
                                    <Table.Td>{n.author}</Table.Td>
                                    <Table.Td>{new Date(n.createdAt).toLocaleString()}</Table.Td>
                                </Table.Tr>
                            ))}
                            {!isLoadingNotes && notes.length === 0 && (
                                <Table.Tr>
                                    <Table.Td colSpan={3}>
                                        <Text size="sm" c={isDark ? "gray.5" : "gray.6"}>
                                            No notes added.
                                        </Text>
                                    </Table.Td>
                                </Table.Tr>
                            )}
                        </Table.Tbody>
                    </Table>
                </div>
                <Group justify="space-between" align="center">
                    <Text size="sm" c={isDark ? "gray.5" : "gray.6"}>
                        Showing {notes.length} of {totalCount} notes
                    </Text>
                    <Pagination total={pageCount} value={page} onChange={onPageChange} size="sm" />
                </Group>
            </Stack>
        </Paper>
    );
}

/**
 * BottomActions
 * Simple action row for saving or cancelling changes.
 */
function BottomActions(props: { onSave: () => void }) {
    const {onSave} = props;
    return (
        <Group justify="flex-end" mt="md">
            <Button className="bg-blue-500 hover:bg-blue-600" onClick={onSave}>
                Save Changes
            </Button>
        </Group>
    );
}


export default function UserEdit() { const MEMO_PAGE_SIZE = 10;
    const {userId} = useParams();
    const navigate = useNavigate();
    const {colorScheme} = useMantineColorScheme();
    const isDark = colorScheme === "dark";

    // Fetch user details using generated GraphQL hook
    // errorPolicy: "all" allows partial data when some fields fail auth (e.g., adminMemos)
    const {data, loading, error, refetch: refetchUser} = useAdminUserByIdQuery({
        variables: userId ? { id: userId } : undefined,
        skip: !userId,
        fetchPolicy: "cache-and-network",
        errorPolicy: "all",
    });

    // Fetch all available roles
    const { data: rolesData } = useGetRolesQuery({ fetchPolicy: "cache-and-network" });

    // GQL mutation to add a memo immediately upon confirmation
    const [adminAddUserMemo, { loading: isAddingMemo } ] = useAdminAddUserMemoMutation();

    // Role mutations
    const [assignRoleToUser, { loading: isAssigningRole }] = useAssignRoleToUserMutation();
    const [unassignRoleFromUser, { loading: isUnassigningRole }] = useUnassignRoleFromUserMutation();

    // Local state derived from API (with safe fallbacks)
    const [name, setName] = useState<string>("");
    const [active, setActive] = useState<boolean>(true);

    // The user controls the username; admins cannot edit it here.

    // Roles - now using RoleInfo objects with IDs
    const [roles, setRoles] = useState<RoleInfo[]>([]);
    const availableRoles: RoleInfo[] = useMemo(() => {
        return (rolesData?.roles ?? [])
            .filter((r): r is NonNullable<typeof r> => r !== null)
            .map(r => ({ id: r.id, name: r.name, color: r.color }));
    }, [rolesData]);
    const [roleToAdd, setRoleToAdd] = useState<string | null>(null);

    /** Adds the selected role (roleToAdd is roleId) to the user via API. */
    async function addRole() {
        if (!roleToAdd || !userId) return;
        try {
            const result = await assignRoleToUser({
                variables: { userId, roleId: roleToAdd },
            });
            if (result.data?.assignRoleToUser?.success) {
                // Refetch user to get updated roles
                await refetchUser();
                setRoleToAdd(null);
                notifications.show({
                    title: "Success",
                    message: "Role assigned successfully",
                    color: "green",
                });
            } else {
                notifications.show({
                    title: "Error",
                    message: result.data?.assignRoleToUser?.errorMessage || "Failed to assign role",
                    color: "red",
                });
            }
        } catch (err: any) {
            notifications.show({
                title: "Error",
                message: err?.message || "An error occurred while assigning the role",
                color: "red",
            });
        }
    }

    /** Removes the specified role from the user via API. */
    async function removeRole(roleId: string) {
        if (!userId) return;
        try {
            const result = await unassignRoleFromUser({
                variables: { userId, roleId },
            });
            if (result.data?.unassignRoleFromUser?.success) {
                // Refetch user to get updated roles
                await refetchUser();
                notifications.show({
                    title: "Success",
                    message: "Role removed successfully",
                    color: "green",
                });
            } else {
                notifications.show({
                    title: "Error",
                    message: result.data?.unassignRoleFromUser?.errorMessage || "Failed to remove role",
                    color: "red",
                });
            }
        } catch (err: any) {
            notifications.show({
                title: "Error",
                message: err?.message || "An error occurred while removing the role",
                color: "red",
            });
        }
    }

    // Notes (admin-only)
    type Note = { id: string; text: string; createdAt: string; author: string };
    const [notes, setNotes] = useState<Note[]>([]);
    const [memoTotalCount, setMemoTotalCount] = useState<number>(0);
    const [newNote, setNewNote] = useState("");
    // Pagination for memos (server-side with fallback to initial page data)
    const [memoPage, setMemoPage] = useState(1);
    const memoPageCount = useMemo(() => Math.max(1, Math.ceil(memoTotalCount / MEMO_PAGE_SIZE)), [memoTotalCount]);

    // Server-side memo paging using generated lazy hook — trigger only for pages beyond the initial page
    const [fetchPagedMemos, { data: pagedMemosData, loading: pagedMemosLoading }] = useAdminUserMemosLazyQuery();
    useEffect(() => {
        if (!userId) return;
        if (memoPage === 1) return; // page 1 handled by initial user query
        fetchPagedMemos({
            variables: { userId, skip: (memoPage - 1) * MEMO_PAGE_SIZE, take: MEMO_PAGE_SIZE },
            fetchPolicy: "cache-and-network",
        });
    }, [userId, memoPage, fetchPagedMemos]);

    // When server-paged memos respond, align the total count for accurate pagination
    useEffect(() => {
        const total = pagedMemosData?.userAdminMemos?.totalCount;
        if (typeof total === 'number' && Number.isFinite(total)) {
            setMemoTotalCount(total);
        }
    }, [pagedMemosData]);

    /** Adds a new note by calling the backend mutation (confirmation handled in UI). */
    async function addNote() {
        const trimmed = newNote.trim();
        if (!trimmed || !userId) return;

        try {
            const result = await adminAddUserMemo({ variables: { userId, memo: trimmed } });
            if (result.data?.addUserMemo) {
                // On success, append to the notes list locally (latest first)
                setNotes((prev) => {
                    const next = [
                        { id: `local-${Date.now()}`, text: trimmed, createdAt: new Date().toISOString(), author: "Admin" },
                        ...prev,
                    ];
                    next.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
                    return next;
                });
                setMemoTotalCount((prev) => (typeof prev === 'number' ? prev + 1 : 1));
                setNewNote("");
                setMemoPage(1);
            } else {
                notifications.show({
                    title: 'Error',
                    message: 'Failed to save note. Please try again.',
                    color: 'red',
                });
            }
        } catch (e) {
            notifications.show({
                title: 'Error',
                message: 'An error occurred while saving the note.',
                color: 'red',
            });
        }
    }

    // Flags (status markers) - stored as boolean fields on user
    const flagOptions = USER_FLAG_OPTIONS;
    const [setUserFlags, { loading: isTogglingFlag }] = useSetUserFlagsMutation();

    // Derive active flags from user's boolean flag fields
    const flags: UserFlag[] = useMemo(() => {
        const user = data?.user;
        if (!user) return [];
        const result: UserFlag[] = [];
        if (user.isBanned) result.push("Banned");
        if (user.isSuspended) result.push("Suspended");
        if (user.isWatchlist) result.push("Watchlist");
        return result;
    }, [data]);

    /** Toggles a flag on the user using the SetUserFlags mutation. */
    async function toggleFlag(flag: UserFlag) {
        if (!userId || isTogglingFlag) return;

        const isCurrentlyActive = flags.includes(flag);
        const newValue = !isCurrentlyActive;

        try {
            const variables: { userId: string; isBanned?: boolean; isSuspended?: boolean; isWatchlist?: boolean } = { userId };
            if (flag === "Banned") variables.isBanned = newValue;
            if (flag === "Suspended") variables.isSuspended = newValue;
            if (flag === "Watchlist") variables.isWatchlist = newValue;

            const result = await setUserFlags({ variables });

            if (result.data?.setUserFlags?.success) {
                await refetchUser();
                notifications.show({
                    title: "Success",
                    message: newValue ? `Applied ${flag} flag` : `Removed ${flag} flag`,
                    color: "green",
                });
            } else {
                notifications.show({
                    title: "Error",
                    message: result.data?.setUserFlags?.errorMessage || `Failed to toggle ${flag} flag`,
                    color: "red",
                });
            }
        } catch (err: any) {
            notifications.show({
                title: "Error",
                message: err?.message || `An error occurred while toggling ${flag} flag`,
                color: "red",
            });
        }
    }

    /** Stub for persisting user changes. Replace it with a real API call later. */
    function onSave() {
        // TODO: Wire to API using { userId, name, active, roles, notes, flags } payload
        navigate("/admin-hub/users");
    }

    // Map server data into local state when it changes
    useEffect(() => {
        const user = data?.user;
        if (!user) return;
        setName(user.name ?? "");
        // Map assignedRoles to RoleInfo objects
        const mappedRoles: RoleInfo[] = (user.assignedRoles ?? [])
            .filter((r): r is NonNullable<typeof r> => r !== null)
            .map(r => ({ id: r.id, name: r.name, color: r.color }));
        setRoles(mappedRoles);

        // Map adminMemos to local Note shape using strongly-typed AdminMemo
        const memos = user.adminMemos ?? [];
        const mappedNotes = memos.map((memo, index) => {
            const id = String(memo?.id ?? index);
            const text = memo?.text ?? "";

            // Prefer explicit operator label, then admin name, else fallback
            const authorName = memo?.operatorLabel || memo?.admin?.name || "System";

            // Normalize timestamp to ISO string to ensure stable rendering
            const createdAtIso = (() => {
                const ts = memo?.timestamp as unknown as string | number | Date | undefined;
                try {
                    return ts ? new Date(ts).toISOString() : new Date().toISOString();
                } catch {
                    return new Date().toISOString();
                }
            })();

            return { id, text, createdAt: createdAtIso, author: authorName };
        });
        // Ensure newest-first ordering by timestamp
        mappedNotes.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        setNotes(mappedNotes);
        setMemoTotalCount((prev) => (mappedNotes.length > 0 ? mappedNotes.length : prev));
    }, [data]);

    return (
        <Container size="xl" px="lg" py="lg" className="tw-bg-page tw-text-default">
            <Stack gap="md">
                <Group justify="flex-start">
                    <Button
                        variant="filled"
                        color="blue"
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        leftSection={<IconArrowLeft size={16} />}
                        onClick={() => navigate('/admin-hub/users')}
                    >
                        Back to Users
                    </Button>
                </Group>
                <Group justify="space-between" align="center">
                    <HeaderSection
                        name={name || data?.user?.name || (loading ? "Loading..." : "-")}
                        userId={userId}
                        isDark={isDark}
                        flags={flags}
                        active={active}
                        onToggleActive={(checked) => setActive(checked)}
                        onFlagInappropriate={() => {
                            // TODO: Wire to API to flag inappropriate username
                        }}
                    />
                    {loading && (
                        <Group gap="xs" align="center">
                            <Loader size="sm"/>
                            <Text size="sm" c={isDark ? "gray.5" : "gray.6"}>
                                Fetching user data...
                            </Text>
                        </Group>
                    )}
                </Group>

                {error && !data?.user && (
                    <Text c="red">Failed to load user data.</Text>
                )}

                <RolesSection
                    roles={roles}
                    availableRoles={availableRoles}
                    roleToAdd={roleToAdd}
                    setRoleToAdd={setRoleToAdd}
                    addRole={addRole}
                    removeRole={removeRole}
                    isDark={isDark}
                    isAddingRole={isAssigningRole}
                    isRemovingRole={isUnassigningRole}
                />

                {/* Flags - administrative status flags stored on user */}
                <FlagsSection
                    flags={flags}
                    flagOptions={flagOptions}
                    toggleFlag={toggleFlag}
                    isDark={isDark}
                    isLoading={isTogglingFlag}
                />

                <NotesSection
                    notes={(() => {
                        // Page 1: render the first page from initial user data (already sorted)
                        if (memoPage === 1) return notes.slice(0, MEMO_PAGE_SIZE);
                        // Other pages: use server-paged memos if available
                        const items = pagedMemosData?.userAdminMemos?.items ?? [];
                        return items.map((memo, index) => {
                            const id = String(memo?.id ?? `p${memoPage}-${index}`);
                            const text = memo?.text ?? "";
                            const author = memo?.operatorLabel || memo?.admin?.name || "System";
                            const createdAt = (() => {
                                const ts = memo?.timestamp as unknown as string | number | Date | undefined;
                                try { return ts ? new Date(ts).toISOString() : new Date().toISOString(); } catch { return new Date().toISOString(); }
                            })();
                            return { id, text, createdAt, author };
                        });
                    })()}
                    totalCount={memoTotalCount}
                    newNote={newNote}
                    setNewNote={setNewNote}
                    addNote={addNote}
                    isAdding={isAddingMemo}
                    isDark={isDark}
                    page={memoPage}
                    pageCount={memoPageCount}
                    onPageChange={(p) => setMemoPage(p)}
                    pageSize={MEMO_PAGE_SIZE}
                    isLoadingNotes={loading || pagedMemosLoading}
                />

                <BottomActions onSave={onSave} />
            </Stack>
        </Container>
    );
}
